import React, { FC, useCallback, useContext, useMemo } from 'react';

import { EvCheckbox, EvSection, EvSelectNew, OptionType } from '@evinced-private/ui-common';

import { getInitialSelectedTimezone, parseTimeStr } from '../../../helpers/SchedulingHelper';
import { FrequencyDto } from '../../../interfaces/ScheduledJobConfiguration';
import { SchedulingSectionContext } from '../context';
import { getSelectedTime } from '../helpers';
import { WEEKDAYS } from '../SchedulingSections.constants';

import { WEEKLY_SCHEDULING_LABELS } from './labels';
import { inputsList } from './WeeklyScheduling.constants';

import './WeeklyScheduling.scss';

export const WeeklyScheduling: FC = () => {
	const { config, updateConfig, error } = useContext(SchedulingSectionContext);

	// Convert config values to UI format
	const selectedWeekdays = useMemo(() => {
		return new Set(config.wdays || []);
	}, [config?.wdays]);

	const selectedTime = useMemo(() => getSelectedTime(config), [config]);

	const selectedTimezone = useMemo(() => {
		return getInitialSelectedTimezone(config?.timezone || '');
	}, [config?.timezone]);

	const onWeekdayClick = (value: number): void => {
		const newWeekdays = new Set(selectedWeekdays);
		if (newWeekdays.has(value)) {
			newWeekdays.delete(value);
		} else {
			newWeekdays.add(value);
		}
		updateConfig({ wdays: Array.from(newWeekdays) });
	};

	const handleTimeChange = useCallback(
		(selectedItem: OptionType[]): void => {
			if (!selectedItem[0]) {
				return;
			}
			const timeStr = selectedItem[0].value as string;
			const { hours, minutes } = parseTimeStr(timeStr);
			updateConfig({ hours: [hours], minutes: [minutes] });
		},
		[updateConfig]
	);

	const handleTimezoneChange = useCallback(
		(selectedItem: OptionType[]): void => {
			if (!selectedItem[0]) {
				return;
			}
			updateConfig({ timezone: selectedItem[0].value as string });
		},
		[updateConfig]
	);

	const inputConfigMap = useMemo(
		() => ({
			selectedTime: {
				selectedValue: selectedTime || [],
				onChange: handleTimeChange
			},
			selectedTimezone: {
				selectedValue: selectedTimezone ? [selectedTimezone] : [],
				onChange: handleTimezoneChange
			}
		}),
		[selectedTime, selectedTimezone, handleTimeChange, handleTimezoneChange]
	);

	const isWeeklySchedulingError = useMemo(() => {
		return error && error.payload?.frequency === FrequencyDto.WEEKLY;
	}, [error]);

	return (
		<EvSection className="weekly-scheduling-section">
			<fieldset className="weekday-checkbox-group-fieldset">
				<legend>{WEEKLY_SCHEDULING_LABELS.daysToRunScan}</legend>
				<div className="weekday-checkbox-group">
					{WEEKDAYS.map((weekday) => {
						const { value, label, id } = weekday;
						const isChecked = selectedWeekdays.has(value);
						return (
							<EvCheckbox
								id={id}
								key={id}
								isChecked={isChecked}
								onClick={() => onWeekdayClick(value)}
								label={label}
								ariaLabel={label}
							/>
						);
					})}
				</div>
			</fieldset>
			<div className="weekday-checkbox-group-error">{isWeeklySchedulingError && error.message}</div>
			<div className="weekly-scheduling-content">
				{inputsList.map((input) => {
					const { id, type, label, placeholder, options, isSearchable, isMultiple } = input;
					const { selectedValue, onChange } = inputConfigMap[type] || {
						selectedValue: [],
						onChange: undefined
					};

					return (
						<EvSelectNew
							key={id}
							id={id}
							options={options}
							selectedValue={selectedValue}
							onSelectChange={onChange}
							placeholder={placeholder}
							label={label}
							isSearchable={isSearchable}
							isMultiple={isMultiple}
							ariaLabel={label}
						/>
					);
				})}
			</div>
		</EvSection>
	);
};
